import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, PageHero, PrimaryLink, OutlineLink } from "@/components/shared";
import { programs } from "@/data/content";

const projectNotes = {
  "namma-mane": "A safe home for senior citizens in need of support — to live with love, respect, peace and affection.",
  thottilu: "For orphaned and helpless children — a haven of quality education, modern skills and a secure childhood, with love and care.",
  tavaru: "A new life for socially exploited women — leading them towards dignity and self-reliance.",
};

const visionStats = [
  { value: "3", label: "Projects Unveiled" },
  { value: "18", label: "Acres of Campuses" },
  { value: "₹120 Cr", label: "Combined Vision" },
  { value: "1,500", label: "Lives in Phase 1" },
];

const MahaKaruna = () => (
  <>
    <PageHero
      testId="mahakaruna-hero"
      eyebrow="Maha Karuna 2026"
      title="Three Projects. One Great Resolve."
      intro="On the sacred stage of Maha Karuna 2026, IBMCT unveiled three landmark humanitarian projects — Namma Mane, Thottilu and Tavaru — a resolve to hold the hands of those living in difficult circumstances and give them not just help, but a safe life, new opportunities and the right to live with self-respect."
    />

    <section className="bg-ivory" data-testid="mahakaruna-story-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow>The Unveiling</Eyebrow>
          <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-charcoal">Compassion, Put Into Action</h2>
          <p className="mt-6 text-bodytext/80 leading-relaxed">
            Maha Karuna 2026 was not just an event; it was a historic step towards putting humanity into action. On one stage, before
            monastics, supporters and well-wishers, three life-changing commitments were made — a home for elders, a childhood for
            children, and a new beginning for women.
          </p>
          <p className="mt-4 text-bodytext/80 leading-relaxed">
            Three projects — three journeys of life — but one Maha Sankalpa: to transform compassion into action, service into resolve,
            and hope into reality in every life they touch.
          </p>
          <blockquote className="mt-8 font-serif text-2xl lg:text-[28px] font-semibold text-charcoal border-l-2 border-saffron pl-6" data-testid="mahakaruna-motto-kn">
            ಮೂರು ಯೋಜನೆಗಳು — ಮೂರು ಬದುಕಿನ ಪಯಣಗಳು, ಆದರೆ ಒಂದೇ ಮಹಾಸಂಕಲ್ಪ
          </blockquote>
          <p className="mt-3 font-sans text-sm text-bodytext/60 pl-6 italic">
            Three projects — three life journeys, but one great resolve.
          </p>
        </Reveal>
        <Reveal delay={100}>
          <div className="img-zoom !rounded-2xl aspect-[4/3]">
            <img
              src="/assets/gallery/g6.webp"
              alt="Lamp-lighting ceremony with monastics and dignitaries at the Maha Karuna 2026 unveiling"
              className="w-full h-full object-cover"
              data-testid="mahakaruna-story-image"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-sandbg" data-testid="mahakaruna-projects-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24">
        <Reveal>
          <Eyebrow>The Three Projects</Eyebrow>
          <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-charcoal">Born on One Stage</h2>
          <p className="mt-4 text-bodytext/75 max-w-2xl">
            Each project answers a different calling — together they form one resolve: a better society built on dignity.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {programs.map((p, i) => (
            <Reveal key={p.id} delay={i * 90} className="h-full">
              <Link
                to={`/our-work/${p.id}`}
                className="group card-ivory !p-0 overflow-hidden h-full flex flex-col no-underline block text-bodytext"
                data-testid={`mahakaruna-project-card-${p.id}`}
              >
                <div className="img-zoom aspect-[4/3] flex items-center justify-center p-8" style={{ backgroundColor: p.emblemBg }}>
                  <img src={p.emblem} alt={`${p.name} emblem`} className="max-w-full max-h-full object-contain" loading="lazy" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="font-serif text-lg text-saffrondeep">{p.knName}</p>
                  <h3 className="mt-1 font-serif text-[26px] font-semibold leading-tight text-charcoal">{p.name}</h3>
                  <p className="mt-3 text-[15px] leading-relaxed text-bodytext/75 flex-1">{projectNotes[p.id]}</p>
                  <span className="link-arrow mt-5 text-[14px]">
                    Explore {p.name} <ArrowRight size={16} aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-charcoal text-white" data-testid="mahakaruna-vision-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
        <Reveal>
          <Eyebrow dark>The Combined Vision</Eyebrow>
          <h2 className="text-[32px] sm:text-4xl lg:text-5xl font-semibold text-white">One Resolve, Measured in Lives</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {visionStats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80}>
              <div className="border-l-2 border-saffron pl-5 text-left" data-testid={`mahakaruna-stat-${i}`}>
                <p className="font-serif text-4xl lg:text-5xl font-semibold text-saffron-light">{s.value}</p>
                <p className="mt-2 font-sans font-semibold text-[rgba(255,255,255,0.82)] text-[14px]">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-coal text-white" data-testid="mahakaruna-quote-section">
      <div className="max-w-4xl mx-auto px-5 lg:px-8 py-20 lg:py-24 text-center">
        <Reveal>
          <div className="thin-sep w-16 mx-auto mb-10" aria-hidden="true" />
          <blockquote className="font-serif text-[24px] sm:text-3xl leading-snug text-white" data-testid="mahakaruna-closing-quote">
            “Namma Mane, Thottilu and Tavaru are not just three projects; they are three assurances for building a better society.”
          </blockquote>
          <p className="mt-8 font-sans text-sm tracking-[0.18em] uppercase text-saffron-light">— Maha Karuna 2026</p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <PrimaryLink to="/donate" testId="mahakaruna-donate-button">Support the Resolve</PrimaryLink>
            <OutlineLink to="/our-work" testId="mahakaruna-explore-button">Explore Our Work</OutlineLink>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default MahaKaruna;
